import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import LearnMore from "./pages/LearnMore";
import GalleryUpload from "./pages/GalleryUpload";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import StaffDashboard from "./pages/StaffDashboard";
import PrincipalDashboard from "./pages/PrincipalDashboard";
import ParentRegister from "./pages/ParentRegister";
import ParentProfile from "./pages/ParentProfile";
import PrincipalRegistration from "./pages/PrincipalRegistration";
import ViewPhotos from "./pages/ViewPhotos";
import CompleteRegistration from "./pages/CompleteRegistration";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Faq from "./pages/Faq";
import ContinueInApp from "./pages/ContinueInApp";
import StaffManagerHandoff from "./pages/StaffManagerHandoff";
import StaffManagerPage from "./pages/StaffManagerPage";
import DeleteAccount from "./pages/DeleteAccount";
import TestSnippet from "./pages/TestSnippet";

import { ROUTES } from "./config";

const AppContent = ({ userRole, handleLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    console.log("🧭 NAV CHECK:", {
      path: location.pathname,
      search: location.search,
      token: !!token,
      userRole,
    });

    // Allow these routes without auto-redirect interference.
    // COMPLETE_REGISTRATION must self-route after successful completion.
    if (
      location.pathname === ROUTES.LEARN_MORE ||
      location.pathname === ROUTES.COMPLETE_REGISTRATION ||
      location.pathname === ROUTES.CONTINUE_IN_APP ||
      location.pathname === ROUTES.STAFF_MANAGER_HANDOFF ||
      location.pathname === ROUTES.TEST_SNIPPET
    ) {
      console.log("✅ Allowed route (no redirect):", location.pathname);
      return;
    }

    if (!token) {
      const allowed = [
        ROUTES.HOME,
        ROUTES.LEARN_MORE,
        ROUTES.LOGIN,
        ROUTES.TERMS,
        ROUTES.PRIVACY,
        ROUTES.DELETE_ACCOUNT,
        ROUTES.FAQ,
        ROUTES.COMPLETE_REGISTRATION,
        ROUTES.PRINCIPAL_REGISTRATION,
        ROUTES.FORGOT_PASSWORD,
        ROUTES.RESET_PASSWORD,
        ROUTES.PARENT_SIGNUP,
        ROUTES.CONTINUE_IN_APP,
        ROUTES.STAFF_MANAGER_HANDOFF,
        ROUTES.TEST_SNIPPET,
      ];

      if (!allowed.includes(location.pathname)) {
        console.log("🔁 REDIRECT (no token) → LOGIN from:", location.pathname);
        navigate(ROUTES.LOGIN, { replace: true });
      } else {
        console.log("✅ Public route allowed (no token):", location.pathname);
      }

      return;
    }

    // If we have a token but no role, force re-login
    if (!userRole) {
      console.log("⚠️ Token present but no role → forcing logout");
      handleLogout();
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    const publicRoutes = new Set([
      ROUTES.HOME,
      ROUTES.LEARN_MORE,
      ROUTES.TERMS,
      ROUTES.PRIVACY,
      ROUTES.FAQ,
      ROUTES.LOGIN,
      // Intentionally excluded:
      // ROUTES.COMPLETE_REGISTRATION
      // ROUTES.CONTINUE_IN_APP
      ROUTES.PRINCIPAL_REGISTRATION,
      ROUTES.FORGOT_PASSWORD,
      ROUTES.RESET_PASSWORD,
      ROUTES.PARENT_SIGNUP,
    ]);

    if (publicRoutes.has(location.pathname)) {
      const target =
        userRole === "ADMIN"
          ? ROUTES.ADMIN
          : userRole === "PRINCIPAL"
            ? ROUTES.PRINCIPAL_DASHBOARD
            : userRole === "STAFF"
              ? ROUTES.STAFF_DASHBOARD
              : ROUTES.VIEW_PHOTOS;

      console.log("🔁 REDIRECT (logged-in on public route)", {
        from: location.pathname,
        to: target,
        role: userRole,
      });

      navigate(`${target}${location.search}`, { replace: true });
    } else {
      console.log("✅ Staying on protected route:", location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, userRole, navigate]);

  return null;
};

const FULL_BLEED_ROUTES = new Set([ROUTES.HOME, ROUTES.LEARN_MORE]);
const LIGHT_ROUTE_SHELL_ROUTES = new Set([ROUTES.LEARN_MORE]);

const RouteShell = ({ children }) => {
  const location = useLocation();
  const isFullBleed = FULL_BLEED_ROUTES.has(location.pathname);
  const toneClass = LIGHT_ROUTE_SHELL_ROUTES.has(location.pathname)
    ? "route-shell--light"
    : "route-shell--dark";

  const shellClassName = [
    "route-shell",
    toneClass,
    isFullBleed ? "route-shell--full" : "container",
  ].join(" ");

  return <div className={shellClassName}>{children}</div>;
};

const App = () => {
  const [userRole, setUserRole] = useState(localStorage.getItem("role") || null);
  const [schoolName, setSchoolName] = useState(localStorage.getItem("schoolName") || null);

  const syncFromStorage = () => {
    setUserRole(localStorage.getItem("role") || null);
    setSchoolName(localStorage.getItem("schoolName") || null);
  };

  useEffect(() => {
    window.addEventListener("gg-auth-changed", syncFromStorage);
    window.addEventListener("storage", syncFromStorage);

    return () => {
      window.removeEventListener("gg-auth-changed", syncFromStorage);
      window.removeEventListener("storage", syncFromStorage);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("role");
    localStorage.removeItem("schoolName");
    setUserRole(null);
    setSchoolName(null);
    window.dispatchEvent(new Event("gg-auth-changed"));
  };

  return (
    <Router>
      <Navbar userRole={userRole} onLogout={handleLogout} />
      <RouteShell>
        <AppContent userRole={userRole} handleLogout={handleLogout} />

        <Routes>
          <Route path={ROUTES.HOME} element={<Home />} />
          <Route path={ROUTES.LEARN_MORE} element={<LearnMore />} />
          <Route path={ROUTES.TERMS} element={<Terms />} />
          <Route path={ROUTES.PRIVACY} element={<Privacy />} />
          <Route path={ROUTES.FAQ} element={<Faq />} />
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.COMPLETE_REGISTRATION} element={<CompleteRegistration />} />
          <Route path={ROUTES.PRINCIPAL_REGISTRATION} element={<PrincipalRegistration />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={ROUTES.PARENT_SIGNUP} element={<ParentRegister />} />
          <Route path={ROUTES.DELETE_ACCOUNT} element={<DeleteAccount />} />
          <Route path={ROUTES.CONTINUE_IN_APP} element={<ContinueInApp />} />
          <Route path={ROUTES.STAFF_MANAGER_HANDOFF} element={<StaffManagerHandoff />} />
          <Route path={ROUTES.TEST_SNIPPET} element={<TestSnippet />} />

          <Route
            path={ROUTES.VIEW_PHOTOS}
            element={
              userRole ? (
                <ViewPhotos userRole={userRole} schoolName={schoolName} />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          <Route
            path={ROUTES.GALLERY_UPLOAD}
            element={
              userRole && ["STAFF", "PRINCIPAL"].includes(userRole) ? (
                <GalleryUpload />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          <Route
            path={ROUTES.ADMIN}
            element={userRole === "ADMIN" ? <Admin /> : <Navigate to={ROUTES.LOGIN} replace />}
          />

          <Route
            path={ROUTES.PRINCIPAL_DASHBOARD}
            element={
              userRole === "PRINCIPAL" ? (
                <PrincipalDashboard />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          <Route
            path={ROUTES.STAFF_MANAGER}
            element={
              userRole === "PRINCIPAL" ? (
                <StaffManagerPage />
              ) : (
                <Navigate to={ROUTES.LOGIN} replace />
              )
            }
          />

          <Route
            path={ROUTES.STAFF_DASHBOARD}
            element={
              userRole === "STAFF" ? <StaffDashboard /> : <Navigate to={ROUTES.LOGIN} replace />
            }
          />

          <Route
            path={ROUTES.PROFILE}
            element={
              userRole === "PARENT" ? <ParentProfile /> : <Navigate to={ROUTES.LOGIN} replace />
            }
          />

          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </RouteShell>
    </Router>
  );
};

export default App;